import * as types from 'src/store/mutation-types'

import { ProjectsClient } from 'src/api/Projects'

// initial state
// shape: [{ id, quantity }]
const state = {
  projects: [],
  projectsMeta: null,
  projectsLoaded: false,
  selectedProject: null
}

// getters
const getters = {
  projects: state => state.projects,
  projectsMeta: state => state.projectsMeta,
  projectsLoaded: state => state.projectsLoaded,
  selectedProject: state => state.selectedProject
}

const paginationDefault = {
  page: 1,
  rowsNumber: 0,
  rowsPerPage: 15,
  descending: true,
  sortBy: 'id',
  filterBy: null,
  include: null
}

const actions = {
  listProjects ({ commit, state }, { pagination } = {}) {
    pagination = { ...paginationDefault, ...(pagination || {}) }

    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']
    commit(types.SET_PROJECTS_LOADED, false)

    return new ProjectsClient()
      .include(pagination.include || [])
      .filter(pagination.filterBy || [])
      .sort(sort)
      .page(pagination.page)
      .limit(pagination.rowsPerPage)
      .list()
      .then(response => {
        commit(types.SET_PROJECTS, response.data.data)
        commit(types.SET_PROJECTS_META, response.data.meta)
        commit(types.SET_PROJECTS_LOADED, true)
      })
      .then(() => Promise.resolve({ projects: state.projects, meta: state.projectsMeta }))
  },

  fetchNextProjectsChunk ({ commit, state }, { done, pagination = {} }) {
    if (!state.projectsLoaded) {
      return done()
    } else if (!state.projectsMeta) {
      return done(true)
    }

    pagination = { ...paginationDefault, ...(pagination || {}) }
    const sort = [pagination.sortBy, pagination.descending ? 'desc' : 'asc']

    let currentPage = state.projectsMeta.pagination.current_page
    let totalPages = state.projectsMeta.pagination.total_pages

    // Check if there is still a chunk to fetch available.
    if (currentPage < totalPages) {
      return new ProjectsClient()
        .include(pagination.include || [])
        .filter(pagination.filterBy || [])
        .sort(sort)
        .limit(pagination.rowsPerPage)
        .page(currentPage + 1)
        .list()
        .then(response => {
          let newProjects = state.projects.concat(response.data.data)

          commit(types.SET_PROJECTS, newProjects)
          commit(types.SET_PROJECTS_META, response.data.meta)

          // Indicate that data has been loaded successfully.
          done()
        })
    } else {
      // No more data available to fetch.
      // Passing true to the callback function tells the infinite scroll component to stop loading.
      done(true)
    }
  },

  searchProjects ({ commit, state }, { pagination, filter }) {
    commit(types.SET_PROJECTS_LOADED, false)

    return new ProjectsClient()
      .include(pagination.include || [])
      .search(filter)
      .then(response => {
        commit(types.SET_PROJECTS, response.data.data)
        commit(types.SET_PROJECTS_META, {
          pagination: {
            count: response.data.data.length,
            current_page: pagination.page,
            per_page: pagination.rowsPerPage,
            total: response.data.data.length,
            total_pages: 1
          }
        })
        commit(types.SET_PROJECTS_LOADED, true)
      })
      .then(() => Promise.resolve({ projects: state.projects, meta: state.projectsMeta }))
  },

  fetchSelectedProject ({ commit }, projectId) {
    return new ProjectsClient()
      .include(['customer', 'statistics', 'invoices', 'invoices.customer', 'quotes', 'quotes.customer', 'expenses.supplier', 'expenses.document'])
      .get(projectId)
      .then(response => {
        commit(types.SET_SELECTED_PROJECT, response.data.data[0])
      })
  },

  createProject ({ commit }, data) {
    return new ProjectsClient()
      .create(data)
      .then(response => {
        const project = response.data.data
        commit(types.ADD_PROJECT, project)

        return project
      })
  },

  updateProject ({ commit }, { projectId, project, include = [] }) {
    return new ProjectsClient()
      .include(include)
      .update(projectId, project)
      .then(response => {
        let project = response.data.data

        commit(types.UPDATE_PROJECT, project)

        return project
      })
  }
}

// mutations
const mutations = {
  [types.SET_PROJECTS] (state, projects) {
    state.projects = projects
  },

  [types.SET_PROJECTS_META] (state, meta) {
    state.projectsMeta = meta
  },

  [types.SET_PROJECTS_LOADED] (state, value) {
    state.projectsLoaded = value
  },

  [types.SET_SELECTED_PROJECT] (state, project) {
    state.selectedProject = project
  },

  [types.ADD_PROJECT] (state, project) {
    state.projects.push(project)
  },

  [types.UPDATE_PROJECT] (state, newProject) {
    let projectIndex = state.projects.findIndex(project => project.id === newProject.id)

    if (projectIndex !== -1) {
      state.projects.splice(projectIndex, 1, Object.assign(state.projects[projectIndex], newProject))
    }

    if (state.selectedProject && state.selectedProject.id === newProject.id) {
      state.selectedProject = Object.assign(state.selectedProject, newProject)
    }
  },

  [types.REMOVE_PROJECT] (state, project) {
    const toDelete = state.projects.find(stateProject => stateProject.id === project.id)
    const index = state.projects.indexOf(toDelete)

    if (index !== -1) {
      state.projects.splice(index, 1)
    }

    if (state.selectedProject && state.selectedProject.id === project.id) {
      state.selectedProject = null
    }
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
